// The Globo Color Swatch app rebuilds the product gallery client side to group media by
// variant. Its grouping rule is "media belongs to the nearest preceding variant image", so a
// video sitting after the last colour's image ends up owned by that one colour. Three things
// go wrong as a result:
//
//   1. Every other colour loses the video slide entirely.
//   2. Thumbnails are left whose `on:click="/select/N"` points past the last remaining slide.
//      They render but do nothing.
//   3. The video thumbnail is pointed at the video file itself instead of its poster image,
//      so it renders as a hole in the strip.
//
// Product video is not colour-specific, so it belongs to every variant. This restores it after
// the colour's images, drops the dead thumbnails, and puts the server's own video thumbnail
// back in place of the app's. It re-derives everything from the slides that actually exist, so
// it re-runs after every rebuild the app does.

const VIDEO_SLIDE = 'deferred-media, video';
// The app rebuilds only on variant change, so repeated restores mean we are fighting it rather
// than following it. Give up instead of looping on the DOM forever.
const MAX_RESTORES = 40;
// Marks the thumbnail this script put back, so a later pass recognises its own work.
const RESTORED = 'videoThumbnailRestored';
// Marks a thumbnail seen pointing at a slide that is not there. Removal cannot be undone, so
// one sighting is not enough: a rebuild landing over several frames looks the same from here.
const DEAD = 'thumbnailDead';
// How long a thumbnail has to stay dead before it is dropped. Long enough for the rest of a
// rebuild to arrive, short enough that nobody sees the thumbnail that goes nowhere.
const SETTLE_MS = 250;

let restores = 0;

/** @param {Element} slide */
const isVideo = (slide) => Boolean(slide.querySelector(VIDEO_SLIDE));

/** @param {Element} thumbnail */
const targetIndex = (thumbnail) => Number((thumbnail.getAttribute('on:click') || '').split('/').pop());

/**
 * Snapshots the video media the server rendered, before the app has a chance to drop it.
 *
 * @param {Element} gallery
 * @returns {{slide: Element, thumbnail: Element}[]}
 */
function captureVideos(gallery) {
  const thumbnails = [...gallery.querySelectorAll('.slideshow-controls__thumbnail')];

  return [...gallery.querySelectorAll('slideshow-slide')].flatMap((slide, index) => {
    const thumbnail = thumbnails[index];
    if (!isVideo(slide) || !thumbnail) return [];

    // Only the theme's own markup is worth keeping: it points at the poster image and carries
    // the play badge. Once the app has rewritten the strip the thumbnail points at the video
    // file instead, and snapshotting that would mean restoring the broken version later.
    const image = thumbnail.querySelector('img');
    if (!image || /\/(shop\/)?videos\//.test(image.src)) return [];

    return [{ slide: slide.cloneNode(true), thumbnail: thumbnail.cloneNode(true) }];
  });
}

/**
 * @param {Element} gallery
 * @param {ReturnType<typeof captureVideos>} videos
 * @returns {{restored: boolean, pending: boolean}} `restored` — a slide was added, which the
 *   slideshow has to be told about. `pending` — a thumbnail is on its first sighting as dead
 *   and has to be looked at again before it can be dropped.
 */
function repair(gallery, videos) {
  const [video] = videos;
  let slides = [...gallery.querySelectorAll('slideshow-slide')];

  // A rebuild empties the slideshow before refilling it. Repairing mid-rebuild would delete
  // every thumbnail, and there is no way back — wait for the next mutation instead.
  if (!slides.length || !video) return { restored: false, pending: false };

  const slidesHost = slides[0].parentElement;
  const thumbnailsHost = gallery.querySelector('.slideshow-controls__thumbnails');
  if (!slidesHost || !thumbnailsHost) return { restored: false, pending: false };

  let restored = false;
  let videoSlide = slides.find(isVideo);

  // The slide survived a settled rebuild, so the app is not taking it back out and we are
  // not looping against it. Start the budget over — otherwise it counts colour switches
  // rather than a fight, and video restoration would stop after the fortieth one.
  if (videoSlide) restores = 0;

  if (!videoSlide && restores < MAX_RESTORES) {
    videoSlide = /** @type {HTMLElement} */ (video.slide.cloneNode(true));
    videoSlide.setAttribute('aria-hidden', 'true');
    slidesHost.append(videoSlide);

    slides = [...gallery.querySelectorAll('slideshow-slide')];
    restored = true;
    restores++;
  }

  // The app can go on adding slides after a repair, which leaves the video stranded in the
  // middle and every thumbnail index after it pointing at the wrong slide. Put it back last.
  if (videoSlide && videoSlide !== slides[slides.length - 1]) {
    slidesHost.append(videoSlide);
    slides = [...gallery.querySelectorAll('slideshow-slide')];
  }

  const videoIndex = videoSlide ? slides.length - 1 : -1;

  if (videoSlide) {
    // Slides are animated off a scroll timeline named after their position. The snapshot
    // carries the name from where the video sat in the full gallery, and the app renumbers the
    // slides it rebuilds, so name ours after the position it actually occupies now.
    const timeline = `--slide-${videoIndex}`;
    const style = /** @type {HTMLElement} */ (videoSlide).style;
    if (style.getPropertyValue('--slideshow-timeline') !== timeline) {
      style.setProperty('--slideshow-timeline', timeline);
      style.setProperty('view-timeline-name', timeline);
    }
  }

  let restoredThumbnail = null;
  let pending = false;

  for (const thumbnail of gallery.querySelectorAll('.slideshow-controls__thumbnail')) {
    const index = targetIndex(thumbnail);
    const dataset = /** @type {HTMLElement} */ (thumbnail).dataset;

    // Keep the one already restored, so a second pass over an untouched gallery is a no-op —
    // otherwise every pass would mutate the DOM and trigger the next one.
    if (index === videoIndex && RESTORED in dataset) {
      restoredThumbnail = thumbnail;
      continue;
    }

    // The app's own video thumbnail goes right away: it is not dead, it is the broken one the
    // server's copy replaces below, poster image and play badge included.
    if (index === videoIndex) {
      thumbnail.remove();
      continue;
    }

    if (Number.isInteger(index) && slides[index]) {
      // The slide it points at turned up after all — a rebuild in progress, not a dead one.
      delete dataset[DEAD];
      continue;
    }

    // Points at a slide that is not there. That is either a leftover the app failed to clean
    // up or a rebuild that has not finished, and the two are indistinguishable in one look, so
    // drop it only if it was already dead when we last looked.
    if (DEAD in dataset) thumbnail.remove();
    else {
      dataset[DEAD] = '';
      pending = true;
    }
  }

  if (videoIndex >= 0 && !restoredThumbnail) {
    const thumbnail = /** @type {HTMLElement} */ (video.thumbnail.cloneNode(true));
    thumbnail.setAttribute('on:click', `/select/${videoIndex}`);
    thumbnail.removeAttribute('aria-selected');
    thumbnail.dataset[RESTORED] = '';
    // The video slide is last, so its thumbnail belongs at the end of the strip.
    thumbnailsHost.append(thumbnail);
  }

  return { restored, pending };
}

/**
 * Slide images are lazy, and the browser decides what to load from where a slide sat when it
 * last looked. The app moves slides around after that, so an image can stay unloaded even once
 * its slide is the one on screen — the slideshow then shows an empty frame. Whatever is being
 * looked at has to load, so take the lazy hint off it.
 *
 * @param {Element} gallery
 */
function loadVisibleMedia(gallery) {
  for (const slide of gallery.querySelectorAll('slideshow-slide[aria-hidden="false"]')) {
    for (const image of slide.querySelectorAll('img[loading="lazy"]')) {
      /** @type {HTMLImageElement} */ (image).loading = 'eager';
    }
  }
}

/**
 * The slideshow decides on connect whether it needs controls, based on how many slides it has.
 * Adding one to a slideshow that came up with a single slide leaves it in the wrong mode, so
 * move it out of the document and back to run its lifecycle again.
 *
 * @param {Element} gallery
 */
function reconnectSlideshow(gallery) {
  const slideshow = gallery.querySelector('slideshow-component');
  const parent = slideshow?.parentNode;
  if (!parent) return;

  const next = slideshow.nextSibling;
  slideshow.remove();
  parent.insertBefore(slideshow, next);
}

// A variant change replaces the <media-gallery> element, so the snapshot cannot live on it. The
// section around it survives, and it is per product — a page showing two products (quick view,
// featured product) must not hand one product's video to the other's gallery.
/** @param {Element} gallery */
const galleryKey = (gallery) => gallery.closest('[id^="shopify-section"]')?.id || 'default';

/** @type {Map<string, ReturnType<typeof captureVideos>>} */
const videosByGallery = new Map();

// The product's video is the same for every variant, so one snapshot per gallery serves them all.
// It is taken from the server-rendered markup, before the app has a chance to rewrite it.
function captureAll() {
  for (const gallery of document.querySelectorAll('media-gallery')) {
    const key = galleryKey(gallery);
    // A gallery with no slides yet is one the server has not finished rendering: there is
    // nothing to snapshot, and recording an empty result would settle the question wrongly.
    if (videosByGallery.has(key) || !gallery.querySelector('slideshow-slide')) continue;

    videosByGallery.set(key, captureVideos(gallery));
  }
}

/** @returns {boolean} Whether a thumbnail is awaiting a second look before it can be dropped. */
function repairAll() {
  let pending = false;

  for (const gallery of document.querySelectorAll('media-gallery')) {
    const result = repair(gallery, videosByGallery.get(galleryKey(gallery)) ?? []);
    if (result.restored) reconnectSlideshow(gallery);
    pending ||= result.pending;
  }

  return pending;
}

function loadAll() {
  for (const gallery of document.querySelectorAll('media-gallery')) loadVisibleMedia(gallery);
}

let queued = false;
let recheck = 0;

function run() {
  loadAll();

  // Nothing on the page has to change for a thumbnail marked dead to be judged, so the second
  // look cannot wait on the next mutation — this page never stops mutating for long enough to
  // rely on that, and the thumbnail would sit there pointing nowhere. Come back on our own.
  if (repairAll()) {
    clearTimeout(recheck);
    recheck = setTimeout(run, SETTLE_MS);
  }
}

// Observe the document rather than the gallery: a variant change can replace the whole
// <media-gallery> element, which would leave an element-scoped observer watching a detached node.
new MutationObserver(() => {
  // Snapshot synchronously, while the gallery is still the server's: the app can rewrite it
  // before the next frame, and there is no second chance at the original markup.
  captureAll();

  if (queued) return;
  queued = true;
  // Coalesce the burst of mutations a rebuild produces, and let it finish first — our own
  // edits land inside this callback, so they are observed too and would otherwise loop.
  requestAnimationFrame(() => {
    queued = false;
    run();
  });
  // aria-hidden is watched too: it is how the slideshow marks the slide on screen, which is the
  // one whose media has to be loaded.
}).observe(document.documentElement, { childList: true, subtree: true, attributeFilter: ['aria-hidden'] });

captureAll();
run();
